import React, { useState, useEffect } from 'react';
import BookingFlow from './BookingFlow';
import KioskCheckIn from './KioskCheckIn';
import KioskCheckOut from './KioskCheckOut';
import KioskWalkIn from './KioskWalkIn';
import KioskRebook from './KioskRebook';
import { setKioskContext, clearKioskContext, kev } from './kioskAnalytics';
import './KioskFlow.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============================================================
// LOCATION CONFIG — maps URL slugs to center IDs
// ============================================================
const KIOSK_LOCATIONS = {
  'lonsdale': {
    name: 'Lonsdale',
    centerId: '68f9a3f8-4927-4808-b91a-68f71a47d771',
  },
  'mountain-hwy': {
    name: 'Mountain Hwy',
    centerId: 'bb3035e6-32a2-451a-95ba-de4f281ceb8e',
  },
  'west-vancouver': {
    name: 'West Vancouver',
    centerId: '099f4638-5eaa-4528-8730-54ee7b22555b',
  },
};

// No hardcoded terminal fallbacks — terminals come from the API (Helcim only)
const KIOSK_TERMINALS = {};

// ============================================================
// KIOSK FLOW — Main wrapper with location routing + employee login
// ============================================================
export default function KioskFlow({ location }) {
  // Demo mode: ?demo=rebook|payment bypasses login and goes straight to checkout
  const urlParams = new URLSearchParams(window.location.search);
  const demoMode = urlParams.get('demo'); // 'rebook', 'walkin', 'payment'
  const isDemo = demoMode === 'rebook' || demoMode === 'walkin' || demoMode === 'payment';
  const [flow, setFlow] = useState(demoMode === 'rebook' || demoMode === 'payment' ? 'checkout' : demoMode === 'walkin' ? 'walkin' : null); // null = home, 'checkin', 'checkout', 'book', 'book_full', 'walkin'

  // Shared guest state — carries between flows so guests don't re-enter credentials
  const [sharedGuest, setSharedGuest] = useState(null);
  const [sharedPhone, setSharedPhone] = useState('');
  const [missedRebook, setMissedRebook] = useState(null); // { guest, appointment } for missed → rebook flow

  const locationConfig = location ? KIOSK_LOCATIONS[location] : null;
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD

  // Employee + Terminal session state — persists in sessionStorage (survives refresh, clears on tab close)
  const [kioskEmployee, setKioskEmployee] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('kiosk-employee')); } catch { return null; }
  });
  const [kioskTerminal, setKioskTerminal] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('kiosk-terminal')); } catch { return null; }
  });
  const [setupStep, setSetupStep] = useState(null); // null (not in setup), 'pin', 'employee', 'terminal'
  const [pinCode, setPinCode] = useState('');
  const [pinError, setPinError] = useState('');
  const [kioskUnlocked, setKioskUnlocked] = useState(() => {
    return sessionStorage.getItem('kiosk-unlocked') === 'true';
  });
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [setupLoading, setSetupLoading] = useState(false);

  // Auth = employee + terminal selected. Resets daily (sessionStorage stores login date).
  const isAuthenticated = !!(kioskEmployee && kioskTerminal);

  // Clear session at start of new day
  useEffect(() => {
    const sessionDate = sessionStorage.getItem('kiosk-session-date');
    if (sessionDate && sessionDate !== today) {
      sessionStorage.removeItem('kiosk-employee');
      sessionStorage.removeItem('kiosk-terminal');
      sessionStorage.removeItem('kiosk-session-date');
      sessionStorage.removeItem('kiosk-unlocked');
      setKioskEmployee(null);
      setKioskTerminal(null);
      setKioskUnlocked(false);
    }
  }, [today]);

  // Show setup when not authenticated (employee/terminal not yet selected)
  const needsSetup = (!isAuthenticated || !kioskUnlocked) && !isDemo && !!locationConfig;

  // Auto-start setup when needed (show PIN, login, or terminal picker)
  useEffect(() => {
    if (!needsSetup) return;
    if (!kioskUnlocked && !setupStep) {
      setSetupStep('pin');
    } else if (!kioskEmployee && !setupStep) {
      startSetup();
    } else if (kioskEmployee && !kioskTerminal && !setupStep) {
      // Employee exists from session but terminal doesn't — go to terminal picker
      setSetupStep('terminal');
      setSetupLoading(true);
      fetch(`${API_URL}/api/kiosk/terminals?centerId=${locationConfig?.centerId}`)
        .then(r => r.json())
        .then(data => {
          const apiTerminals = data.terminals || [];
          setTerminals(apiTerminals.length > 0 ? apiTerminals : (KIOSK_TERMINALS[location] || []));
          setSetupLoading(false);
        })
        .catch(() => { setTerminals(KIOSK_TERMINALS[location] || []); setSetupLoading(false); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSetup]);

  function goHome() {
    clearKioskContext();
    setFlow(null);
    setSharedGuest(null);
    setSharedPhone('');
    setMissedRebook(null);
  }

  function startFlow(f) {
    setFlow(f);
    setKioskContext({ centerId: locationConfig?.centerId, flow: f });
    kev('home', 'flow_start', { flow: f });
  }

  // ============================================================
  // SETUP HANDLERS — Employee login + Terminal selection (auth gate)
  // ============================================================
  // Auto-submit PIN when 4 digits entered
  useEffect(() => {
    if (pinCode.length === 4) handlePinSubmit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinCode]);

  function handlePinSubmit() {
    if (pinCode === '8008') {
      setKioskUnlocked(true);
      sessionStorage.setItem('kiosk-unlocked', 'true');
      setPinCode('');
      setPinError('');
      startSetup();
    } else {
      setPinError('Incorrect code');
      setPinCode('');
    }
  }

  function startSetup() {
    setSetupStep('employee');
    setLoginUsername('');
    setLoginPassword('');
    setLoginError(null);
  }

  async function handleLogin() {
    if (!loginUsername.trim() || !loginPassword.trim()) return;
    setSetupLoading(true);
    setLoginError(null);
    try {
      const res = await fetch(`${API_URL}/api/kiosk/employee-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUsername.trim(), password: loginPassword, centerId: locationConfig.centerId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setLoginError(data.error || 'Invalid username or password');
        setSetupLoading(false);
        return;
      }
      const employee = { id: data.employee.id, name: data.employee.name, token: data.token };
      setKioskEmployee(employee);
      sessionStorage.setItem('kiosk-employee', JSON.stringify(employee));
      // Load terminals
      setSetupStep('terminal');
      const termRes = await fetch(`${API_URL}/api/kiosk/terminals?centerId=${locationConfig.centerId}`);
      const termData = await termRes.json();
      const apiTerminals = termData.terminals || [];
      setTerminals(apiTerminals.length > 0 ? apiTerminals : (KIOSK_TERMINALS[location] || []));
      setSetupLoading(false);
    } catch (e) {
      console.error('Login failed:', e);
      setLoginError('Could not connect. Please try again.');
      setSetupLoading(false);
    }
  }

  function selectTerminal(terminal) {
    setKioskTerminal(terminal);
    sessionStorage.setItem('kiosk-terminal', JSON.stringify(terminal));
    sessionStorage.setItem('kiosk-session-date', today);
    setSetupStep(null);
    // Non-blocking: try to set reader preference in Zenoti
    fetch(`${API_URL}/api/kiosk/set-terminal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employeeId: kioskEmployee?.id, cardReaderId: terminal.id, centerId: locationConfig.centerId }),
    }).catch(() => {});
  }

  function switchSession() {
    sessionStorage.removeItem('kiosk-employee');
    sessionStorage.removeItem('kiosk-terminal');
    sessionStorage.removeItem('kiosk-session-date');
    sessionStorage.removeItem('kiosk-unlocked');
    setKioskEmployee(null);
    setKioskTerminal(null);
    setKioskUnlocked(false);
    setSetupStep(null);
  }

  // ============================================================
  // LOCATION PICKER — shown when no location in URL (/kiosk)
  // ============================================================
  if (!location) {
    return (
      <div className="kiosk-home-container">
        <div className="kiosk-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/kiosk-bg.png)` }}>
          <div className="kiosk-hero-overlay" />
          <div className="kiosk-location-picker kiosk-fade-in">
            <div className="kiosk-location-picker-title">Select Location</div>
            {Object.entries(KIOSK_LOCATIONS).map(([slug, loc]) => (
              <button
                key={slug}
                className="kiosk-location-card"
                onClick={() => { window.location.href = `/kiosk/${slug}`; }}
              >
                {loc.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // INVALID LOCATION
  // ============================================================
  if (!locationConfig) {
    return (
      <div className="kiosk-container">
        <KioskHeader locationName="" />
        <div className="kiosk-main" style={{ alignItems: 'center', justifyContent: 'center', paddingTop: 80 }}>
          <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Location not found</div>
          <div className="kiosk-screen-sub">Check the URL and try again</div>
          <button className="kiosk-btn-primary" style={{ marginTop: 24 }} onClick={() => { window.location.href = '/kiosk'; }}>
            Choose Location
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // SETUP SCREENS — Employee + Terminal picker (auth gate)
  // ============================================================
  if (needsSetup || setupStep) {
    if (setupStep === 'pin') {
      return (
        <div className="kiosk-container">
          <KioskHeader locationName={locationConfig.name} />
          <div className="kiosk-main">
            <div className="kiosk-screen kiosk-setup-screen kiosk-fade-in">
              <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Enter Access Code</div>
              <div className="kiosk-screen-sub" style={{ marginTop: 0 }}>{locationConfig.name} Kiosk</div>
              <div className="kiosk-pin-dots" style={{ display: 'flex', gap: 12, justifyContent: 'center', margin: '24px 0 8px' }}>
                {[0,1,2,3].map(i => (
                  <div key={i} style={{
                    width: 16, height: 16, borderRadius: '50%',
                    background: i < pinCode.length ? '#D4577C' : '#e0e0e0',
                    transition: 'background 0.15s'
                  }} />
                ))}
              </div>
              {pinError && <div className="kiosk-login-error">{pinError}</div>}
              <KioskKeypad
                mode="numeric"
                value={pinCode}
                onChange={v => { setPinError(''); setPinCode(v); }}
                onSubmit={handlePinSubmit}
                maxLength={4}
              />
            </div>
          </div>
        </div>
      );
    }

    if (setupStep === 'employee') {
      return (
        <div className="kiosk-container">
          <KioskHeader locationName={locationConfig.name} />
          <div className="kiosk-main">
            <div className="kiosk-screen kiosk-setup-screen kiosk-fade-in">
              <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Staff Login</div>
              <div className="kiosk-screen-sub" style={{ marginTop: 0 }}>{locationConfig.name} Kiosk</div>
              <div className="kiosk-login-form">
                <input
                  className="kiosk-login-input"
                  type="text"
                  placeholder="Zenoti Username"
                  value={loginUsername}
                  onChange={e => setLoginUsername(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && document.querySelector('.kiosk-login-password')?.focus()}
                  autoFocus
                  autoComplete="username"
                />
                <div className="kiosk-login-password-wrap">
                  <input
                    className="kiosk-login-input kiosk-login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    autoComplete="current-password"
                  />
                  <button type="button" className="kiosk-login-show-pw" onClick={() => setShowPassword(s => !s)}>
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {loginError && <div className="kiosk-login-error">{loginError}</div>}
                <button
                  className="kiosk-btn-primary"
                  style={{ width: '100%', marginTop: 8 }}
                  onClick={handleLogin}
                  disabled={setupLoading || !loginUsername.trim() || !loginPassword.trim()}
                >
                  {setupLoading ? 'Signing in...' : 'Sign In'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (setupStep === 'terminal') {
      return (
        <div className="kiosk-container">
          <KioskHeader locationName={locationConfig.name} />
          <div className="kiosk-main">
            <div className="kiosk-screen kiosk-setup-screen kiosk-fade-in">
              <div className="kiosk-screen-title" style={{ fontSize: 24 }}>Select Payment Terminal</div>
              <div className="kiosk-screen-sub" style={{ marginTop: 0 }}>Signed in as {kioskEmployee?.name}</div>
              {setupLoading ? (
                <div className="kiosk-loading-dots" style={{ marginTop: 40 }}>
                  <div className="kiosk-loading-dot" /><div className="kiosk-loading-dot" /><div className="kiosk-loading-dot" />
                </div>
              ) : terminals.length > 0 ? (
                <div className="kiosk-setup-grid">
                  {terminals.map(t => (
                    <button key={t.id} className="kiosk-setup-card" onClick={() => selectTerminal(t)}>
                      <div className="kiosk-setup-card-name">{t.name}</div>
                      <div className="kiosk-setup-card-sub">{t.model}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', marginTop: 40 }}>
                  <div className="kiosk-screen-sub">No terminals configured for this location</div>
                  <button className="kiosk-btn-primary" style={{ marginTop: 20 }} onClick={() => selectTerminal({ id: 'none', name: 'No Terminal', model: '' })}>
                    Continue Without Terminal
                  </button>
                </div>
              )}
              <button className="kiosk-btn-text" style={{ marginTop: 16 }} onClick={() => { setSetupStep('employee'); setKioskEmployee(null); sessionStorage.removeItem('kiosk-employee'); }}>
                ← Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // ============================================================
  // AUTHENTICATED — normal kiosk flow
  // ============================================================
  const centerId = locationConfig.centerId;
  // Set center context for analytics (flow is set per-flow via startFlow)
  setKioskContext({ centerId });

  // Book picker — Today or Another Day?
  if (flow === 'book') {
    return (
      <div className="kiosk-home-container">
        <div className="kiosk-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/kiosk-bg.png)` }}>
          <div className="kiosk-hero-overlay" />
          <div className="kiosk-location-picker kiosk-fade-in">
            <div className="kiosk-location-picker-title">When would you like to come in?</div>
            <button className="kiosk-location-card" onClick={() => startFlow('walkin')}>
              Today
            </button>
            <button className="kiosk-location-card" onClick={() => startFlow('book_full')}>
              Another Day
            </button>
            <button className="kiosk-location-card" onClick={() => startFlow('rebook_last')}>
              Rebook Last Visit
            </button>
            <button className="kiosk-hero-back-link" onClick={goHome}>
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Full booking flow — renders existing BookingFlow with a back button
  if (flow === 'book_full') {
    return (
      <div className="kiosk-booking-wrapper">
        <button className="kiosk-booking-back-btn" onClick={goHome}>
          ← Back to Kiosk
        </button>
        <BookingFlow source="kiosk" />
      </div>
    );
  }

  // Rebook last visit — phone lookup → last visit services → rebook flow
  if (flow === 'rebook_last') {
    return <KioskRebook onBack={goHome} centerId={centerId} employee={kioskEmployee} terminal={kioskTerminal} />;
  }

  // Walk-in flow — simplified same-day booking
  if (flow === 'walkin') {
    return <>
      {isDemo && <div className="kiosk-demo-banner">DEMO MODE — Not connected to real bookings</div>}
      <KioskWalkIn onBack={goHome} centerId={centerId} initialGuest={sharedGuest} initialPhone={sharedPhone}
        employee={kioskEmployee} terminal={kioskTerminal}
        onBookAnotherDay={(guestData, services) => {
          setMissedRebook({ guest: guestData, services: services.map(s => ({ id: s.id, name: s.name, price: s.price, duration: s.duration, selected: true, preSelected: true })) });
          setFlow('checkout');
        }} />
    </>;
  }

  // Check-in flow
  if (flow === 'checkin') {
    return <KioskCheckIn onBack={goHome} onBook={(guestData, phoneNum) => {
      if (guestData) setSharedGuest(guestData);
      if (phoneNum) setSharedPhone(phoneNum);
      setFlow('walkin');
    }} onBookFull={(guestData, apptData) => {
      if (guestData && apptData) {
        setMissedRebook({ guest: guestData, appointment: apptData });
        setFlow('checkout');
      } else {
        setFlow('book_full');
      }
    }} centerId={centerId} employee={kioskEmployee} terminal={kioskTerminal} />;
  }

  // Checkout flow
  if (flow === 'checkout') {
    return <>
      {isDemo && <div className="kiosk-demo-banner">DEMO MODE — Not connected to real bookings</div>}
      <KioskCheckOut onBack={goHome} centerId={centerId} initialRebook={missedRebook} employee={kioskEmployee} terminal={kioskTerminal} />
    </>;
  }

  // Home screen — photo background with logo and bold white buttons
  return (
    <div className="kiosk-home-container">
      {/* Full-bleed background image */}
      <div className="kiosk-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/kiosk-bg.png)` }}>
        <div className="kiosk-hero-overlay" />

        {/* Buttons pushed to bottom */}
        <div className="kiosk-hero-actions kiosk-fade-in" style={{ animationDelay: '0.15s' }}>
          <button className="kiosk-hero-card" onClick={() => startFlow('checkin')}>
            <div className="kiosk-hero-card-label">CHECK IN</div>
          </button>
          <button className="kiosk-hero-card" onClick={() => startFlow('checkout')}>
            <div className="kiosk-hero-card-label">CHECK OUT</div>
          </button>
        </div>

        {/* Book button */}
        <div className="kiosk-hero-book-row kiosk-fade-in" style={{ animationDelay: '0.3s' }}>
          <button className="kiosk-hero-card kiosk-hero-card-book" onClick={() => startFlow('book')}>
            <div className="kiosk-hero-card-label">BOOK NOW</div>
            <div className="kiosk-hero-card-sub">No appointment? Walk right in</div>
          </button>
        </div>

      </div>
    </div>
  );
}

// Shared header used by all kiosk screens
export function KioskHeader({ locationName }) {
  const d = new Date();
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return (
    <div className="kiosk-header">
      <img src="/kiosk-logo.png" alt="Cutie Pie Wax Bar" className="kiosk-logo" />
      <div className="kiosk-header-right">
        {locationName && <div className="kiosk-header-location">{locationName}</div>}
        <div className="kiosk-header-time">{day} · {time}</div>
      </div>
    </div>
  );
}

// Shared step dots component
export function StepDots({ steps, current }) {
  const idx = steps.indexOf(current);
  if (idx < 0) return null;
  const labels = { invoice: 'Invoice', rebook: 'Rebook', tip: 'Tip', payment: 'Pay', rating: 'Review' };
  return (
    <div className="kiosk-steps-bar">
      <div className="kiosk-steps-track">
        <div className="kiosk-steps-track-fill" style={{ width: `${(idx / (steps.length - 1)) * 100}%` }} />
      </div>
      <div className="kiosk-steps-labels">
        {steps.map((s, i) => (
          <div key={s} className={`kiosk-step-label ${i === idx ? 'active' : i < idx ? 'done' : ''}`}>
            {labels[s] || s}
          </div>
        ))}
      </div>
    </div>
  );
}

// Shared helpers
export function formatPhone(val) {
  const d = val.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function formatPrice(n) { return '$' + n.toFixed(2); }

// ============================================================
// KIOSK KEYPAD — On-screen keyboard for iPad kiosk mode
// ============================================================
export function KioskKeypad({ mode = 'numeric', value = '', onChange, onSubmit, maxLength }) {
  const [shifted, setShifted] = useState(false);

  function handleKey(key) {
    if (key === 'backspace') {
      onChange(value.slice(0, -1));
    } else if (key === 'clear') {
      onChange('');
    } else if (key === 'submit') {
      onSubmit?.();
    } else if (key === 'shift') {
      setShifted(s => !s);
    } else if (key === 'space') {
      if (maxLength && value.length >= maxLength) return;
      onChange(value + ' ');
    } else {
      if (maxLength && value.length >= maxLength) return;
      const char = shifted ? key.toUpperCase() : key;
      onChange(mode === 'numeric' ? value + key : value + char);
      if (shifted) setShifted(false);
    }
  }

  if (mode === 'numeric') {
    return (
      <div className="kiosk-keypad">
        <div className="kiosk-keypad-row">
          {['1','2','3'].map(k => <button key={k} className="kiosk-key" onClick={() => handleKey(k)}>{k}</button>)}
        </div>
        <div className="kiosk-keypad-row">
          {['4','5','6'].map(k => <button key={k} className="kiosk-key" onClick={() => handleKey(k)}>{k}</button>)}
        </div>
        <div className="kiosk-keypad-row">
          {['7','8','9'].map(k => <button key={k} className="kiosk-key" onClick={() => handleKey(k)}>{k}</button>)}
        </div>
        <div className="kiosk-keypad-row">
          <button className="kiosk-key kiosk-key-fn" onClick={() => handleKey('clear')}>Clear</button>
          <button className="kiosk-key" onClick={() => handleKey('0')}>0</button>
          <button className="kiosk-key kiosk-key-fn" onClick={() => handleKey('backspace')}>⌫</button>
        </div>
      </div>
    );
  }

  // Alpha keyboard (QWERTY) with a digit row on top — needed for alphanumeric inputs
  // like gift card codes. Digits are benign on the other alpha call sites (names,
  // comments), and the existing handleKey path already accepts any character key.
  // 'email' mode is identical except it adds a row with @ . _ - and inserts a
  // common domain shortcut when tapped.
  const digitRow = ['1','2','3','4','5','6','7','8','9','0'];
  const rows = [
    ['q','w','e','r','t','y','u','i','o','p'],
    ['a','s','d','f','g','h','j','k','l'],
    ['z','x','c','v','b','n','m']
  ];

  function insertChunk(chunk) {
    if (maxLength && value.length + chunk.length > maxLength) return;
    onChange(value + chunk);
  }

  return (
    <div className="kiosk-keypad kiosk-keypad-alpha">
      <div className="kiosk-keypad-row">
        {digitRow.map(k => (
          <button key={k} className="kiosk-key kiosk-key-letter" onClick={() => handleKey(k)}>{k}</button>
        ))}
      </div>
      {rows.map((row, i) => (
        <div className="kiosk-keypad-row" key={i}>
          {i === 2 && <button className={`kiosk-key kiosk-key-fn kiosk-key-shift ${shifted ? 'active' : ''}`} onClick={() => handleKey('shift')}>⇧</button>}
          {row.map(k => (
            <button key={k} className="kiosk-key kiosk-key-letter" onClick={() => handleKey(k)}>
              {shifted ? k.toUpperCase() : k}
            </button>
          ))}
          {i === 2 && <button className="kiosk-key kiosk-key-fn" onClick={() => handleKey('backspace')}>⌫</button>}
        </div>
      ))}
      {mode === 'email' ? (
        <>
          <div className="kiosk-keypad-row">
            {['@','.','_','-'].map(k => (
              <button key={k} className="kiosk-key kiosk-key-letter" onClick={() => handleKey(k)}>{k}</button>
            ))}
            <button className="kiosk-key kiosk-key-shortcut" onClick={() => insertChunk('.com')}>.com</button>
            <button className="kiosk-key kiosk-key-shortcut" onClick={() => insertChunk('.ca')}>.ca</button>
          </div>
          <div className="kiosk-keypad-row">
            <button className="kiosk-key kiosk-key-shortcut" onClick={() => insertChunk('@gmail.com')}>@gmail.com</button>
            <button className="kiosk-key kiosk-key-shortcut" onClick={() => insertChunk('@hotmail.com')}>@hotmail.com</button>
            <button className="kiosk-key kiosk-key-shortcut" onClick={() => insertChunk('@outlook.com')}>@outlook.com</button>
          </div>
        </>
      ) : (
        <div className="kiosk-keypad-row">
          <button className="kiosk-key kiosk-key-space" onClick={() => handleKey('space')}>space</button>
        </div>
      )}
    </div>
  );
}
